import React, { useCallback, useState } from 'react'
import message from 'antd/lib/message'
import { loadStripe } from '@stripe/stripe-js'
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'

import config from 'config'

import { CloseIcon, PrimaryButton } from 'components/index'
import Overlay from 'components/AuthModal/components/Overlay'
import Modal from 'components/AuthModal/components/Modal'
import { OVERLAY } from 'components/AuthModal/styles'

const stripePromise = loadStripe(config.stripe.public_key)

const AUTOFILL_STYLE = {
  '-webkit-box-shadow': '0 0 0 30px #171A1E inset !important',
  '-webkit-text-fill-color': 'white !important',
  color: 'white',
}

const CheckoutForm = ({ clientSecret, onComplete }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [isElementsLoaded, setIsElementLoaded] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault()
      const card = elements?.getElement(CardElement)
      if (!elements || !stripe) {
        return
      }

      setIsSubmitting(true)
      try {
        const method = clientSecret.startsWith('pi') ? 'confirmCardPayment' : 'confirmCardSetup'
        const resp = await stripe[method](clientSecret, {
          payment_method: {
            card,
          },
        })

        if (resp.error) {
          message.error(resp.error.message)
          return
        }

        setTimeout(() => {
          setIsSubmitting(false)
          onComplete()
        }, 3000)
      } catch (error) {
        message.error(error.message || 'Please try again')
        setIsSubmitting(false)
      }
    },
    [elements, stripe, clientSecret, onComplete]
  )

  return (
    <form className="h-full flex flex-col justify-between" onSubmit={handleSubmit}>
      <div className="mb-10 rounded-full bg-woodsmokeBlack px-6 overflow-x-hidden">
        {!isElementsLoaded && <div>Loading</div>}
        <CardElement
          onReady={() => setIsElementLoaded(true)}
          options={{
            hidePostalCode: true,
            style: {
              base: {
                fontSize: '16px',
                color: 'white',
                lineHeight: '52px',
                backgroundColor: '#171A1E',
                borderRadius: '10px !important',
                '::placeholder': {
                  color: 'white',
                },
                ':-webkit-autofill': AUTOFILL_STYLE,
                ':-webkit-autofill:hover': AUTOFILL_STYLE,
                ':-webkit-autofill:active': AUTOFILL_STYLE,
                ':-webkit-autofill:focus': AUTOFILL_STYLE,
                ':-input:-internal-autofill-selected': AUTOFILL_STYLE,
              },
            },
          }}
        />
      </div>
      {isElementsLoaded && (
        <PrimaryButton
          type="submit"
          onClick={handleSubmit}
          loading={isSubmitting}
          className="w-full bg-secondaryGradient"
          loadingColor="text-black"
          textColor="text-black"
        >
          {!isSubmitting && 'COMPLETE'}
        </PrimaryButton>
      )}
    </form>
  )
}

const PaymentModal = ({ show, onClose, plan, clientSecret, onComplete }) => {
  if (!clientSecret || !show || !plan) {
    return null
  }

  return (
    <Overlay show={show} className={OVERLAY} preventEscBackdropClose>
      <Modal innerClassName="overflow-y-scroll flex flex-col rounded-t-3xl overflow-hidden md:rounded-xl">
        <div className="relative flex flex-col text-white items-stretch h-full w-full p-4 pb-8 bg-codGray md:py-8 md:px-7 md:w-108 md:max-h-screen">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xl font-semibold font-ownersWide">Payment Information</div>
            <button
              className="bg-white bg-opacity-20 w-6 h-6 rounded-full flex items-center justify-center"
              onClick={() => onClose()}
            >
              <CloseIcon className="w-2 h-2 text-white" />
            </button>
          </div>
          <div className="flex-grow">
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                hidePostalCode: true,
                appearance: {
                  theme: 'night',
                },
              }}
            >
              <CheckoutForm clientSecret={clientSecret} onComplete={onComplete} />
            </Elements>
          </div>
        </div>
      </Modal>
    </Overlay>
  )
}

export default PaymentModal
